import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '@/hooks/use-scroll-animation';
import { playClick, playHover } from '@/utils/sounds';
import { subscribeManyMedia, getManyMedia } from '@/lib/collections/media';
import type { MediaResponse } from '@/lib/collections/media';
import { useRealtimeData } from '@/hooks/use-realtime-data';

// Detect if a URL is a YouTube link
function isYouTubeUrl(url: string): boolean {
  return url.includes('youtube.com') || url.includes('youtu.be');
}

// Extract video ID from various YouTube URL formats
function extractYouTubeId(url: string): string | null {
  if (url.includes('/embed/')) {
    const match = url.match(/\/embed\/([a-zA-Z0-9_-]{11})/);
    if (match) return match[1];
  }
  if (url.includes('youtu.be/')) {
    const match = url.match(/youtu\.be\/([a-zA-Z0-9_-]{11})/);
    if (match) return match[1];
  }
  if (url.includes('youtube.com/watch')) {
    const match = url.match(/[?&]v=([a-zA-Z0-9_-]{11})/);
    if (match) return match[1];
  }
  return null;
}

const FilmStrip: React.FC = () => (
  <div className="flex items-center gap-1 px-2 py-1.5" style={{ background: '#111' }}>
    {Array.from({ length: 12 }).map((_, i) => (
      <div
        key={i}
        className="rounded-sm shrink-0"
        style={{ width: 14, height: 10, background: '#222', border: '1px solid #333' }}
      />
    ))}
  </div>
);

const VideoCard: React.FC<{ video: MediaResponse }> = ({ video }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [inView, setInView] = useState(false);
  const [muted, setMuted] = useState(true);
  // Native video state (non-YouTube only)
  const [playing, setPlaying] = useState(true);
  const [nativeMuted, setNativeMuted] = useState(true);

  const videoId = isYouTubeUrl(video.url) ? extractYouTubeId(video.url) : null;

  // IntersectionObserver — load iframe only when scrolled into view
  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Native video controls
  const handleNativePlayPause = useCallback(() => {
    if (!videoRef.current) return;
    playClick();
    if (playing) {
      videoRef.current.pause();
      setPlaying(false);
    } else {
      videoRef.current.play();
      setPlaying(true);
    }
  }, [playing]);

  const handleNativeMute = useCallback(() => {
    if (!videoRef.current) return;
    playClick();
    videoRef.current.muted = !nativeMuted;
    setNativeMuted(!nativeMuted);
  }, [nativeMuted]);

  const handleToggleMute = useCallback(() => {
    if (!iframeRef.current?.contentWindow) return;
    playClick();
    if (muted) {
      iframeRef.current.contentWindow.postMessage(
        '{"event":"command","func":"unMute","args":""}',
        '*'
      );
      setMuted(false);
    } else {
      iframeRef.current.contentWindow.postMessage(
        '{"event":"command","func":"mute","args":""}',
        '*'
      );
      setMuted(true);
    }
  }, [muted]);

  return (
    <div className="w-full max-w-2xl md:max-w-5xl mx-auto">
      {/* Video title */}
      {video.title && (
        <div className="text-center mb-6">
          <h3
            className="text-3xl md:text-4xl font-bold uppercase"
            style={{
              fontFamily: "'Bangers', cursive",
              letterSpacing: '0.08em',
              color: '#ffffff',
              textShadow: '2px 2px 0 rgba(0,0,0,0.7), 0 0 20px rgba(0,0,0,0.5)',
            }}
          >
            {video.title}
          </h3>
        </div>
      )}

      {/* Cinema frame wrapper */}
      <div style={{ position: 'relative', transform: 'rotate(-1deg)', transformOrigin: 'center center' }}>
        {/* "NOW PLAYING" tag positioned on top-left corner of card */}
        <div
          className="absolute z-30 pointer-events-none"
          style={{ top: '-12px', left: '12px' }}
        >
          <div
            aria-hidden="true"
            style={{
              display: 'inline-block',
              background: '#E63946',
              color: '#fff',
              fontFamily: "'Bangers', cursive",
              fontSize: '1.35rem',
              letterSpacing: '0.12em',
              padding: '3px 15px',
              border: '4.5px solid rgba(0,0,0,0.2)',
              boxShadow: '4.5px 4.5px 0 rgba(0,0,0,0.25)',
              transform: 'rotate(-5deg)',
              userSelect: 'none',
              lineHeight: 1.3,
            }}
          >
            NOW PLAYING
          </div>
        </div>

        <div
          style={{
            background: '#0a0a0a',
            border: '5px solid #1a1a1a',
            borderRadius: '10px',
            boxShadow:
              '6px 6px 0 rgba(0,0,0,0.9), 0 0 0 1px rgba(230,57,70,0.25), 0 20px 60px rgba(0,0,0,0.8), inset 0 0 40px rgba(0,0,0,0.5)',
            overflow: 'hidden',
          }}
        >
          {/* Film strip top */}
          <div style={{ borderBottom: '2px solid #222' }}>
            <FilmStrip />
          </div>

          {/* Video area — 16:9 container with scroll-triggered autoplay iframe */}
          <div
            ref={containerRef}
            style={{ position: 'relative', width: '100%', paddingBottom: '56.25%', overflow: 'hidden', background: '#000' }}
          >
            {videoId ? (
              inView && (
                <>
                  <iframe
                    ref={iframeRef}
                    src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1&enablejsapi=1`}
                    title={video.title ?? 'Danger Dan Teaser'}
                    allow="autoplay; encrypted-media"
                    allowFullScreen
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      width: '120%',
                      height: '120%',
                      transform: 'translate(-50%, -50%)',
                      border: 'none',
                      pointerEvents: 'none',
                    }}
                  />
                  {/* Unmute / mute toggle button */}
                  <button
                    onClick={handleToggleMute}
                    onMouseEnter={playHover}
                    aria-label={muted ? 'Unmute video' : 'Mute video'}
                    className="video-mute-btn"
                    style={{
                      position: 'absolute',
                      bottom: 12,
                      right: 12,
                      zIndex: 10,
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      background: 'rgba(0,0,0,0.6)',
                      border: '1.5px solid rgba(255,255,255,0.25)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'border-color 0.2s ease, background 0.2s ease',
                      backdropFilter: 'blur(4px)',
                    }}
                  >
                    {muted ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                        <line x1="23" y1="9" x2="17" y2="15" />
                        <line x1="17" y1="9" x2="23" y2="15" />
                      </svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                        <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                        <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                      </svg>
                    )}
                  </button>
                </>
              )
            ) : (
              <>
                <video
                  ref={videoRef}
                  src={video.url}
                  autoPlay
                  muted
                  loop
                  playsInline
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    display: 'block',
                  }}
                />
                {/* Custom controls — inside video area, bottom-right */}
                <div
                  style={{
                    position: 'absolute',
                    bottom: 12,
                    right: 12,
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '8px',
                    zIndex: 20,
                  }}
                >
                  {/* Play / Pause */}
                  <button
                    onClick={handleNativePlayPause}
                    onMouseEnter={playHover}
                    aria-label={playing ? 'Pause video' : 'Play video'}
                    className="ctrl-btn"
                    title={playing ? 'Pause' : 'Play'}
                  >
                    {playing ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                        <rect x="5" y="3" width="4" height="18" rx="1" />
                        <rect x="15" y="3" width="4" height="18" rx="1" />
                      </svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                        <polygon points="5,3 19,12 5,21" />
                      </svg>
                    )}
                  </button>

                  {/* Mute / Unmute */}
                  <button
                    onClick={handleNativeMute}
                    onMouseEnter={playHover}
                    aria-label={nativeMuted ? 'Unmute video' : 'Mute video'}
                    className={nativeMuted ? 'ctrl-btn ctrl-btn-pulse' : 'ctrl-btn'}
                    title={nativeMuted ? 'Unmute' : 'Mute'}
                  >
                    {nativeMuted ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="white" stroke="none" />
                        <line x1="23" y1="9" x2="17" y2="15" />
                        <line x1="17" y1="9" x2="23" y2="15" />
                      </svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="white" stroke="none" />
                        <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                        <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                      </svg>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Film strip bottom */}
          <div style={{ borderTop: '2px solid #222' }}>
            <FilmStrip />
          </div>
        </div>

      </div>

      {/* TV button + caption below video */}
      <div className="flex flex-col items-center gap-2 mt-6">
        <Link
          to="/series"
          onClick={playClick}
          onMouseEnter={e => {
            playHover();
            const el = e.currentTarget as HTMLAnchorElement;
            el.style.transform = 'rotate(1deg) scale(1.06) translateY(-3px)';
            el.style.boxShadow = '7px 7px 0 rgba(0,0,0,0.9), 0 0 36px rgba(124,252,0,0.5)';
          }}
          onMouseLeave={e => {
            const el = e.currentTarget as HTMLAnchorElement;
            el.style.transform = 'rotate(1deg) scale(1) translateY(0)';
            el.style.boxShadow = '5px 5px 0 rgba(0,0,0,0.85), 0 0 24px rgba(124,252,0,0.35)';
          }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '10px',
            fontFamily: "'Bangers', cursive",
            fontSize: 'clamp(1.4rem, 4vw, 2rem)',
            letterSpacing: '0.12em',
            color: '#0a0a0f',
            background: '#7CFC00',
            border: '4px solid rgba(0,0,0,0.85)',
            boxShadow: '5px 5px 0 rgba(0,0,0,0.85), 0 0 24px rgba(124,252,0,0.35)',
            padding: '8px 28px',
            textDecoration: 'none',
            transform: 'rotate(1deg)',
            transition: 'transform 0.18s ease, box-shadow 0.18s ease',
            cursor: 'pointer',
            userSelect: 'none',
          }}
        >
          {/* TV icon */}
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="2" y="7" width="20" height="15" rx="2" ry="2" />
            <polyline points="17 2 12 7 7 2" />
          </svg>
          DANGER DAN TV (ANIMATED SERIES)
        </Link>
        <p
          className="text-center text-xs uppercase tracking-[0.2em]"
          style={{ color: 'rgba(255,255,255,0.3)', fontFamily: "'Bangers', cursive", textShadow: '0 1px 3px rgba(0,0,0,0.4)' }}
        >
        </p>
      </div>

    </div>
  );
};

export const VideoTeaser: React.FC = () => {
  const sectionRef = useScrollAnimation<HTMLElement>(0.05);
  const [videoEntry, setVideoEntry] = useState<MediaResponse | null>(null);
  const [loaded, setLoaded] = useState(false);

  // Fast initial fetch
  useEffect(() => {
    getManyMedia()
      .then((entries) => {
        const video = entries.find((m: MediaResponse) => m.type === 'video') ?? null;
        setVideoEntry(video);
        setLoaded(true);
      })
      .catch(() => {
        setLoaded(true);
      });
  }, []);

  // Also subscribe for live updates (admin changes)
  const { data: mediaEntries } = useRealtimeData<MediaResponse[]>(subscribeManyMedia, true);

  // Update from subscription when it arrives
  useEffect(() => {
    if (mediaEntries) {
      const video = mediaEntries.find((m) => m.type === 'video') ?? null;
      setVideoEntry(video);
      setLoaded(true);
    }
  }, [mediaEntries]);

  if (!loaded || !videoEntry) return null;

  return (
    <section
      ref={sectionRef}
      className="scroll-fade-up relative z-10 py-20 px-6"
      style={{ opacity: 1, transform: 'none' }}
    >
      <div className="relative" style={{ zIndex: 1 }}>
        <VideoCard video={videoEntry} />
      </div>

      <style>{`
        .video-mute-btn:hover {
          border-color: #7CFC00 !important;
          background: rgba(0,0,0,0.75) !important;
        }
        .ctrl-btn {
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(10, 60, 10, 0.9);
          border: 3px solid #1a3a1a;
          border-radius: 6px;
          cursor: pointer;
          box-shadow: 3px 3px 0 rgba(0,0,0,0.7), 0 0 0 1px rgba(124,252,0,0.12);
          transition: border-color 0.15s ease, background 0.15s ease, transform 0.1s ease;
          backdrop-filter: blur(4px);
        }
        .ctrl-btn:hover {
          border-color: #7CFC00;
          background: rgba(15, 80, 15, 0.95);
          transform: translateY(-1px);
          box-shadow: 3px 4px 0 rgba(0,0,0,0.8), 0 0 8px rgba(124,252,0,0.2);
        }
        .ctrl-btn:active {
          transform: translateY(1px);
          box-shadow: 1px 1px 0 rgba(0,0,0,0.7);
        }
        @keyframes pulse-glow {
          0%, 100% { box-shadow: 3px 3px 0 rgba(0,0,0,0.7), 0 0 8px rgba(124,252,0,0.3); border-color: #7CFC00; }
          50% { box-shadow: 3px 3px 0 rgba(0,0,0,0.7), 0 0 16px rgba(124,252,0,0.6); border-color: #7CFC00; }
        }
        .ctrl-btn-pulse {
          animation: pulse-glow 2s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

